import { formatQty, parseQty } from '@/components/sklad/types';
import { ApiError, apiFetch } from '@/lib/apiFetch';
import { useCallback, useEffect, useState } from 'react';

type Roll = {
  id: number;
  serial_number: number;
  latka_name: string;
  latka_image: string;
  unit: string;
  stock_qty: number;
};

type Group = { id: string; name: string; image: string; rolls: Roll[] };

const INPUT =
  'text-sm text-text bg-surface border border-border rounded-lg px-3 py-2 outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 transition-all';

/**
 * Vyskladnění látky rovnou z detailu výrobní zakázky — pro každou látku produktu
 * nabídne role, které jsou na skladě, a odečte z vybrané. Do historie role se
 * zapíše ID zakázky, takže je později vidět, na co se metráž spotřebovala.
 */
export function VyskladneniLatky({
  productCode,
  productId,
  orderRef,
  onMovement,
  onRollUsed,
}: {
  /** Kód produktu (uložená zakázka). */
  productCode?: string;
  /** Tabidoo id produktu — u nové zakázky, kde kód ještě nemáme. */
  productId?: string;
  /** ID zakázky do historie role; u nové zakázky prázdné, doplní se po uložení. */
  orderRef: string;
  /** Hlásí ID zapsaného pohybu, aby k němu šlo po uložení doplnit zakázku. */
  onMovement?: (movementId: number) => void;
  /** Číslo právě vyskladněné role — doplní se do „Číslo látky" zakázky. */
  onRollUsed?: (serialNumber: number) => void;
}) {
  const [groups, setGroups] = useState<Group[] | null>(null);
  // Kolik metrů už na tuhle zakázku padlo — přes 0 se ukáže „Vyskladněno".
  const [issued, setIssued] = useState(0);

  const load = useCallback(async () => {
    const param = productId
      ? `product_id=${encodeURIComponent(productId)}`
      : productCode
        ? `code=${encodeURIComponent(productCode)}`
        : '';
    if (!param) {
      setGroups([]);
      return;
    }
    const ref = orderRef ? `&order_ref=${encodeURIComponent(orderRef)}` : '';
    try {
      const d = await apiFetch<{ groups: Group[]; issued: number }>(
        `/api/sklad/product-rolls?${param}${ref}`,
      );
      setGroups(d.groups);
      // U nové zakázky ještě není podle čeho hledat — příznak drží odečet níž.
      if (d.issued > 0) setIssued(d.issued);
    } catch {
      setGroups([]);
    }
  }, [productCode, productId, orderRef]);

  useEffect(() => {
    load();
  }, [load]);

  if (groups === null) {
    return <p className="text-sm text-text-muted">Načítám látky produktu…</p>;
  }
  if (groups.length === 0) {
    return (
      <p className="text-sm text-text-muted">
        K tomuto produktu není v databázi produktů navázaná žádná látka.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {issued > 0 && (
        // Rohový proužek: čtvercové okno u rohu ho ořízne, uvnitř leží pás
        // pevné šířky s vycentrovaným textem — proto se text nikdy neusekne.
        <div className="pointer-events-none absolute top-0 right-0 w-36 h-36 overflow-hidden">
          <div className="absolute top-[26px] right-[-42px] w-[170px] rotate-45 bg-action text-white text-[11px] font-bold uppercase tracking-wider text-center py-1 shadow-sm">
            Vyskladněno
          </div>
        </div>
      )}
      {groups.map((g) => (
        <LatkaRow
          key={g.id}
          group={g}
          orderRef={orderRef}
          onDone={load}
          onMovement={onMovement}
          onRollUsed={onRollUsed}
          onIssued={(qty) => setIssued((n) => n + qty)}
        />
      ))}
    </div>
  );
}

/** Jedna látka: náhled, výběr role, kolik odečíst. */
function LatkaRow({
  group,
  orderRef,
  onDone,
  onMovement,
  onRollUsed,
  onIssued,
}: {
  group: Group;
  orderRef: string;
  onDone: () => void;
  onMovement?: (movementId: number) => void;
  onRollUsed?: (serialNumber: number) => void;
  onIssued: (qty: number) => void;
}) {
  const [rollId, setRollId] = useState<number | ''>(group.rolls[0]?.id ?? '');
  const [amount, setAmount] = useState('');
  const [wholeRoll, setWholeRoll] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ text: string; kind: 'ok' | 'err' } | null>(null);

  const roll = group.rolls.find((r) => r.id === rollId) ?? null;

  // Po odečtu se seznam rolí načte znovu — zdělaná role z něj zmizí. Kdyby
  // `rollId` zůstalo na ní, select by ukazoval jinou roli, než která je vybraná,
  // a další odečet by nešel odeslat. Držíme výběr na tom, co reálně existuje.
  useEffect(() => {
    if (group.rolls.some((r) => r.id === rollId)) return;
    setRollId(group.rolls[0]?.id ?? '');
    setWholeRoll(false);
    setAmount('');
  }, [group.rolls, rollId]);

  /** „Zdělat celou látku" = odečíst celý zbytek role; odečet na nulu ji zdělá. */
  function toggleWholeRoll(checked: boolean, target = roll) {
    setWholeRoll(checked);
    setAmount(checked && target ? String(target.stock_qty) : '');
  }
  const amountNum = parseQty(amount);
  const preview =
    roll && Number.isFinite(amountNum)
      ? Math.max(0, Math.round((roll.stock_qty - amountNum) * 100) / 100)
      : null;

  async function submit() {
    if (!roll) {
      setMsg({ text: 'Vyber roli, ze které se má odečítat', kind: 'err' });
      return;
    }
    if (!wholeRoll && (!Number.isFinite(amountNum) || amountNum <= 0)) {
      setMsg({ text: 'Zadej, kolik metrů odečíst', kind: 'err' });
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      const res = await apiFetch<{
        stock_qty: number;
        subtracted: number;
        spent: boolean;
        movement_id: number | null;
      }>(`/api/sklad/pieces/${roll.id}/subtract`, {
        method: 'POST',
        // U „zdělat celou látku" zbytek dopočítá server z aktuálního stavu.
        body: wholeRoll
          ? { spend_rest: true, order_ref: orderRef }
          : { amount: amountNum, order_ref: orderRef },
      });
      if (res.movement_id) onMovement?.(res.movement_id);
      onRollUsed?.(roll.serial_number);
      onIssued(res.subtracted);
      setMsg({
        text: res.spent
          ? `Odečteno ${formatQty(res.subtracted, roll.unit)} z role č. ${roll.serial_number} — role je zdělaná.`
          : `Odečteno ${formatQty(res.subtracted, roll.unit)} z role č. ${roll.serial_number}, zbývá ${formatQty(res.stock_qty, roll.unit)}.`,
        kind: 'ok',
      });
      setAmount('');
      setWholeRoll(false);
      onDone();
    } catch (e: unknown) {
      setMsg({
        text: e instanceof ApiError ? e.describe() : 'Odečtení selhalo',
        kind: 'err',
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 py-2">
      {group.image ? (
        <img
          src={group.image}
          alt=""
          className="w-11 h-11 rounded-lg object-cover border border-border bg-bg shrink-0"
        />
      ) : (
        <div className="w-11 h-11 rounded-lg bg-bg border border-border shrink-0" />
      )}

      <div className="min-w-[150px] max-w-[240px]">
        <div className="text-sm font-semibold text-text leading-tight break-words">
          {group.name || 'Bez názvu'}
        </div>
        <div className="text-[11px] text-text-muted">
          {group.rolls.length > 0
            ? `${group.rolls.length} ${group.rolls.length === 1 ? 'role' : 'rolí'} na skladě`
            : 'Není na skladě'}
        </div>
      </div>

      {group.rolls.length === 0 ? (
        <div className="text-xs text-text-secondary bg-bg border border-border rounded-lg px-2.5 py-1.5">
          Žádné role této látky na skladě.
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-1.5">
            <select
              value={rollId}
              onChange={(e) => {
                const next = Number(e.target.value);
                setRollId(next);
                // Při přepnutí role přepočítej „celou látku" na její zbytek.
                if (wholeRoll) {
                  toggleWholeRoll(true, group.rolls.find((r) => r.id === next) ?? null);
                }
              }}
              className={`${INPUT} cursor-pointer`}
            >
              {group.rolls.map((r) => (
                <option key={r.id} value={r.id}>
                  Role č. {r.serial_number} — {formatQty(r.stock_qty, r.unit)}
                </option>
              ))}
            </select>
            <label className="flex items-center gap-1.5 cursor-pointer select-none w-fit">
              <input
                type="checkbox"
                checked={wholeRoll}
                onChange={(e) => toggleWholeRoll(e.target.checked)}
                className="w-4 h-4 rounded border-border cursor-pointer accent-primary"
              />
              <span className="text-xs font-medium text-text-secondary">Zdělat celou látku</span>
            </label>
          </div>
          <div className="flex items-center gap-1.5">
            <input
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              inputMode="decimal"
              placeholder="Kolik"
              disabled={wholeRoll}
              className={`${INPUT} w-24 text-right disabled:opacity-60 disabled:cursor-not-allowed`}
            />
            <span className="text-sm text-text-muted">m</span>
          </div>
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            className="bg-action hover:bg-action-dark text-white text-sm font-semibold rounded-lg px-4 py-2 cursor-pointer transition-colors disabled:opacity-50"
          >
            {busy ? 'Odečítám…' : wholeRoll ? 'Zdělat roli' : 'Vyskladnit'}
          </button>
          {preview !== null &&
            (preview === 0 ? (
              <span className="text-sm font-semibold text-danger">Role bude zdělaná</span>
            ) : (
              <span className="text-sm text-text-secondary">
                zbude <strong className="text-text">{formatQty(preview, roll?.unit ?? 'm')}</strong>
              </span>
            ))}
        </>
      )}

      {msg && (
        <div
          className={`basis-full text-xs font-medium ${
            msg.kind === 'ok' ? 'text-action-dark' : 'text-danger'
          }`}
        >
          {msg.text}
        </div>
      )}
    </div>
  );
}
